/**
 * Les en-têtes et le corps d'un envoi JSON — et pourquoi l'en-tête suit le corps, jamais l'inverse.
 *
 * ## Le défaut que ceci corrige *(signalé par Chris le 26/08/2026 : « j'ai rajouté un chapitre 4
 * dans aqualia par erreur, quand je clique sur retirer cela ne le supprime pas ? »)*
 *
 * `envoyer` posait `Content-Type: application/json` sur **tout** envoi, et confiait le corps à
 * `JSON.stringify(corps)`. Or `JSON.stringify(undefined)` ne rend pas `"null"` ni `""` : il rend
 * **`undefined`**, et `fetch` n'envoie alors aucun corps. La requête annonçait donc du JSON et
 * n'en portait pas — combinaison que Fastify 5 refuse **avant d'entrer dans la route** :
 *
 *     FST_ERR_CTP_EMPTY_JSON_BODY
 *     « Body cannot be empty when content-type is set to 'application/json' »  → 400
 *
 * Relevé dans le journal du service de production : les trois clics de Chris sur « Retirer »
 * ont bien envoyé `DELETE /api/versions/…/chapitres/4`, et les trois ont été rejetés en 400
 * sans que la route s'exécute. Rien n'était supprimé, et le message affiché à l'écran était
 * celui du framework — illisible pour qui n'a pas la pile sous les yeux.
 *
 * Un seul appel sur neuf était touché : `supprimerChapitreCree` passait `undefined`, les huit
 * autres suppressions passent `{}` — qui sérialise en `"{}"`, un corps valide. **C'est ce qui
 * rendait le défaut si discret : il n'y avait aucune raison de soupçonner l'enveloppe.**
 *
 * ## La règle
 *
 * Pas de corps, pas d'annonce de corps. `Accept` reste posé dans tous les cas : c'est ce qu'on
 * veut RECEVOIR, et une suppression rend bien du JSON.
 *
 * Le test porte sur `undefined`, mais la condition est plus large et c'est voulu :
 * `JSON.stringify` rend `undefined` pour tout ce qu'il ne sait pas sérialiser — une fonction,
 * un symbole. Aucun de ces cas ne doit annoncer un corps qui n'arrivera pas.
 */
export type EnvoiJson = {
  readonly headers: Record<string, string>;
  /** Absent quand il n'y a rien à envoyer — et l'en-tête `Content-Type` l'est alors aussi. */
  readonly body?: string;
};

export function envoiJson(corps: unknown): EnvoiJson {
  const serialise = JSON.stringify(corps);
  if (serialise === undefined) {
    return { headers: { Accept: "application/json" } };
  }
  return {
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: serialise,
  };
}
